import { apiFetch } from "@/shared/api/http";
import { createUuid } from "@/shared/utils/uuid";

export interface StudentAgendaItem {
  cancelada?:boolean;
  turmaId: string;
  horarioTurmaId: string;
  turmaNome: string;
  modalidade: string;
  data: string;
  horaInicio: string;
  horaFim: string;
  quando: string;
  ausenciaInformada: boolean;
  motivoAusencia?: string | null;
  presencaConfirmada: boolean;
  podeGerarCheckIn: boolean;
  podeInformarAusencia: boolean;
  podeCancelarAusencia: boolean;
}

export interface StudentAgendaCompetition {
  campeonatoId: string;
  inscricaoId: string;
  nome: string;
  data: string;
  local?: string | null;
  modalidadeId: string;
  modalidade: string;
  categoria: string;
  faixa?: string | null;
  categoriaPeso?: string | null;
  status: string;
  resultado?: string | null;
  colocacao?: number | null;
}

export interface StudentAgendaMonth {
  inicio: string;
  fim: string;
  hoje: string;
  aulas: StudentAgendaItem[];
  competicoes: StudentAgendaCompetition[];
}

export interface StudentFrequencyItem {
  id: string;
  turmaId: string;
  turmaNome: string;
  data: string;
  status: "Presente" | "Ausente" | "Justificado" | string;
  observacao?: string | null;
}

export interface StudentFinancialItem {
  id: string;
  descricao: string;
  valorOriginal: number;
  valorPago: number;
  saldoAberto: number;
  vencimento: string;
  status: string;
  vencido: boolean;
  origem: string;
  podeInformarPagamento: boolean;
  pagamentoInformadoPendente: boolean;
  podePagarPix?:boolean;
}

export interface StudentFinancialDetails {
  situacao: string;
  saldoAberto: number;
  titulosEmAberto: number;
  titulosVencidos: number;
  titulos: StudentFinancialItem[];
}

export function fetchStudentAgenda(days = 30) {
  return apiFetch<StudentAgendaItem[]>(`/api/app/aluno/agenda?dias=${days}`);
}

export function fetchStudentAgendaMonth(year: number, month: number) {
  return apiFetch<StudentAgendaMonth>(`/api/app/aluno/agenda/mes?ano=${year}&mes=${month}`);
}

export function fetchStudentFrequency(limit = 30) {
  return apiFetch<StudentFrequencyItem[]>(`/api/app/aluno/frequencia?limite=${limit}`);
}

export function fetchStudentFinancialDetails(studentId?: string) {
  return studentId
    ? apiFetch<StudentFinancialDetails>(`/api/app/responsavel/alunos/${studentId}/financeiro`)
    : apiFetch<StudentFinancialDetails>("/api/app/aluno/financeiro");
}

export function informStudentPayment(input: { contaReceberId:string; valor:number; formaPagamento:string; dataPagamento:string; observacao?:string; comprovante?:File }, studentId?:string) {
  const form = new FormData();
  form.set("ContaReceberId", input.contaReceberId);
  form.set("Valor", String(input.valor));
  form.set("FormaPagamento", input.formaPagamento);
  form.set("DataPagamento", input.dataPagamento);
  form.set("ChaveIdempotencia", createUuid());
  if(input.observacao) form.set("Observacao", input.observacao);
  if(input.comprovante) form.set("Comprovante", input.comprovante);
  const path = studentId ? `/api/app/responsavel/alunos/${studentId}/financeiro/informar-pagamento` : "/api/app/aluno/financeiro/informar-pagamento";
  return apiFetch<string>(path,{method:"POST",body:form});
}


export function informStudentAbsence(item: StudentAgendaItem, motivo?: string) {
  return apiFetch<void>("/api/app/aluno/agenda/ausencia", { method: "POST", body: JSON.stringify({ turmaId: item.turmaId, horarioTurmaId: item.horarioTurmaId, data: item.data, motivo: motivo?.trim() || null }) });
}

export function cancelStudentAbsence(item: StudentAgendaItem) {
  return apiFetch<void>("/api/app/aluno/agenda/ausencia/cancelar", { method: "POST", body: JSON.stringify({ turmaId: item.turmaId, horarioTurmaId: item.horarioTurmaId, data: item.data }) });
}

export interface StudentCheckInQr { token: string; expiraEmUtc: string; renovarEmSegundos: number; qrCodeDataUrl: string; }
export function fetchStudentCheckInQr(item: StudentAgendaItem) {
  const query = new URLSearchParams({
    turmaId: item.turmaId,
    horarioTurmaId: item.horarioTurmaId,
    data: item.data,
  });
  return apiFetch<StudentCheckInQr>(`/api/app/aluno/check-in-token?${query.toString()}`);
}

export interface StudentLessonJourneyHistoryItem { tipo:string; titulo:string; detalhe?:string|null; emUtc?:string|null; }
export interface StudentLessonJourneyEvaluation { registradaEmUtc:string; tecnica?:string|null; tema?:string|null; observacao?:string|null; tipoRegistro:string; estadoRapido?:string|null; }
export interface StudentLessonJourneyMessage { id:string; titulo:string; mensagem:string; criadoEmUtc:string; }
export interface StudentLessonJourneyMaterial { id:string; tipo:string|number; titulo:string; url:string; visivelParaAluno:boolean; }
export interface StudentLessonJourneyContent { recursoEnsinoId:string; nome:string; tema:string; categoria?:string|null; tipo:string|number; ensinado:boolean; }
export interface StudentLessonJourneyMomentMedia { momentoId:string; midiaId:string; contentType:string; ordem:number; }
export interface StudentLessonJourneyMoment { id:string; legenda?:string|null; publicadoEmUtc:string; midias:StudentLessonJourneyMomentMedia[]; }
export interface StudentLessonJourney {
  alunoId:string; turmaId:string; horarioTurmaId:string; sessaoId?:string|null; data:string; horaInicio:string; horaFim:string;
  turmaNome:string; modalidade:string; professor?:string|null; statusAula:string; statusPresenca:string; ausenciaInformada:boolean;
  motivoAusencia?:string|null; podeGerarCheckIn:boolean; podeInformarAusencia:boolean; podeCancelarAusencia:boolean;
  tema?:string|null; objetivo?:string|null; resumo?:string|null; conteudos:StudentLessonJourneyContent[]; materiais:StudentLessonJourneyMaterial[];
  momentos:StudentLessonJourneyMoment[]; avaliacoesCompartilhadas:StudentLessonJourneyEvaluation[]; recados:StudentLessonJourneyMessage[]; historico:StudentLessonJourneyHistoryItem[];
}

export function fetchStudentLessonJourney(item: StudentAgendaItem, studentId?: string) {
  const query = new URLSearchParams({ turmaId:item.turmaId, horarioTurmaId:item.horarioTurmaId, data:item.data });
  const path = studentId ? `/api/app/responsavel/alunos/${studentId}/agenda/aula?${query}` : `/api/app/aluno/agenda/aula?${query}`;
  return apiFetch<StudentLessonJourney>(path);
}

export function fetchResponsibleAgendaMonth(studentId:string, year:number, month:number) {
  return apiFetch<StudentAgendaMonth>(`/api/app/responsavel/alunos/${studentId}/agenda/mes?ano=${year}&mes=${month}`);
}

export interface PixPagamento {modo:string;payload:string;qrCodeDataUrl:string;valor:number;nomeRecebedor:string;chave:string;referencia:string;confirmacaoManual:boolean;expiraEmUtc:string|null}
export function fetchStudentPix(contaId:string,studentId?:string){return apiFetch<PixPagamento>(studentId?`/api/app/responsavel/alunos/${studentId}/financeiro/${contaId}/pix`:`/api/app/aluno/financeiro/${contaId}/pix`)}

export const fetchStudentFinancialHistoryPanel=(id:string,pagina=1)=>apiFetch<import("../gestor/gestorApi").GestorAlunoHistoricoFinanceiro>(id==="self"?`/api/app/aluno/financeiro/historico?pagina=${pagina}`:`/api/app/responsavel/alunos/${id}/financeiro/historico?pagina=${pagina}`);
